import { Trans } from '@lingui/react';
import React from 'react';
import { EditParticipantDialog } from './Edit';
import { StepOrSubscribersDocument, useStepOrSubscribersSuspenseQuery } from './or-subscribers.graphql';
import { AlertDialog } from '../../../../../../components/simple-dialogs/AlertDialog';
import { Participant } from './Participant';
import { companyToIdentityInput } from '@startuptools/business-ui/identity/identity-to-input';
import { PaymentKind } from '@startuptools/common/participants';
import { type GqlCreateIdentityInput, GqlWarrantProgramKind } from '../../../../../../graphql/base-types.graphql';
import { JuridicalKind } from '../../../../../../models/identity.model';
import { AddIdentity } from '@startuptools/business-ui/identity/add/AddIdentity';
import { DetailState, InputState } from '@startuptools/business-ui/identity/types';
import { ErrorDisplay } from '../../../../../../components/error-display/ErrorDisplay';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Alert } from '@startuptools/ui/Alert';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Stack } from '@startuptools/ui/Stack';
import { ErrorBoundary } from '@startuptools/ui/ErrorBoundary';
import { WarrantProgramDocument } from '../../../../WarrantProgram.graphql';
import { BigNumber } from 'bignumber.js';
import { Tooltip } from '@startuptools/ui/joy/Tooltip';
import { useStepper } from '../../../../../../components/steppers/common/StepperContext';
import { OptionsRegistrationDocument } from '../../../../OptionsRegistration.graphql';

export const OrStepSubscribers = ({
  orId,
  wpId,
  navigateToDecisionStep,
}: {
  orId: string;
  wpId: string;
  navigateToDecisionStep: () => void;
}) => {
  const companyId = useCompanyId();
  const {
    data: { warrantProgram, optionsRegistration, optionsRegistrationErrors },
  } = useStepOrSubscribersSuspenseQuery({ variables: { orId, wpId, companyId }, fetchPolicy: 'cache-and-network' });
  const { stepNext } = useStepper();
  const [showIdentityDialog, setShowIdentityDialog] = React.useState(false);
  const [error, setError] = React.useState<React.ReactNode | undefined>();
  const [identity, setIdentity] = React.useState<GqlCreateIdentityInput | undefined>(undefined);

  const handleContinue = () => {
    if (warrantProgram.warrantKind !== GqlWarrantProgramKind.Employee) {
      return stepNext();
    }
    navigateToDecisionStep();
  };

  const fullyAllocated = new BigNumber(warrantProgram.allocated).gte(warrantProgram.maxNumberOfOptions ?? '0');
  return (
    <Box>
      <Stack gap={2}>
        <Stack direction="row" justifyContent="space-between">
          <Tooltip title={fullyAllocated ? <Trans id="Fully allocated" /> : undefined}>
            <span>
              <Button
                disabled={fullyAllocated}
                onClick={async () => {
                  if (!warrantProgram.paymentKindCash) {
                    return setError(
                      <Trans id="Since the decision does not allow for cash payment, you can not add a subscriber here. Go back to the decision if you wish to add subscribers via in-kind payment or settlement" />,
                    );
                  }
                  if (warrantProgram.warrantsForQeso) {
                    if (optionsRegistration.participants.length > 0) {
                      return setError(<Trans id="A Warrants for QESO program can only have 1 subscriber" />);
                    }
                    return setIdentity(companyToIdentityInput(warrantProgram.company));
                  }
                  setShowIdentityDialog(true);
                }}
              >
                <Trans id="Add subscriber" />
              </Button>
            </span>
          </Tooltip>
        </Stack>
        <Stack gap={2} mt={2}>
          {optionsRegistration.participants.map(p => (
            <Participant
              key={p.id}
              participant={p}
              /* TODO: we should maybe start with error codes */
              oversubscribed={optionsRegistrationErrors.options?.error?.en.short === 'Over allocated'}
              or={optionsRegistration}
              company={warrantProgram.company}
              warrantProgram={warrantProgram}
              refetchQueries={[StepOrSubscribersDocument, WarrantProgramDocument, OptionsRegistrationDocument]}
              navigateToDecisionStep={navigateToDecisionStep}
            />
          ))}
        </Stack>
        <ErrorDisplay
          error={optionsRegistrationErrors.participants?.error}
          severity={optionsRegistrationErrors.participants?.severity}
        />

        {warrantProgram.usesStepper && (
          <Box>
            <Button onClick={handleContinue}>
              <Trans id="Continue" />
            </Button>
          </Box>
        )}
      </Stack>

      {showIdentityDialog && (
        <AddIdentity
          headline={<Trans id="Add subscriber" />}
          juridicalKinds={
            warrantProgram.warrantKind === GqlWarrantProgramKind.Employee
              ? [JuridicalKind.Person]
              : [JuridicalKind.Person, JuridicalKind.Company]
          }
          availableInputStates={{
            [InputState.System]: true,
            [InputState.CompanyBoard]: false,
            [InputState.Swedish]: true,
            [InputState.Foreign]: true,
          }}
          detailFieldsSettings={{
            countryCode: warrantProgram.warrantKind === GqlWarrantProgramKind.Wise ? DetailState.Required : undefined,
          }}
          handleCreate={identity => {
            setIdentity(identity);
            setShowIdentityDialog(false);
          }}
          onClose={() => {
            setShowIdentityDialog(false);
          }}
        />
      )}
      {identity && (
        <ErrorBoundary showModal>
          <EditParticipantDialog
            optionRegistrationId={orId}
            paymentKind={PaymentKind.Cash}
            closeDialog={() => {
              setIdentity(undefined);
            }}
            warrantProgram={warrantProgram}
            identity={identity}
            company={warrantProgram.company}
            refetchQueries={[StepOrSubscribersDocument, WarrantProgramDocument]}
          />
        </ErrorBoundary>
      )}

      {error && (
        <AlertDialog
          title={<Trans id="Can't add subscribers here" />}
          acceptButtonText={<Trans id="Close" />}
          onAccept={() => {
            setError(undefined);
          }}
        >
          <Alert color="neutral">{error}</Alert>
        </AlertDialog>
      )}
    </Box>
  );
};
